import { useState } from 'react';
import {
  ErrorBanner,
  GroupCard,
  ListSkeleton,
  OptionRow,
  SectionFootnote,
  SectionHeader,
  useToast,
} from '../components';
import { downloadExport, useMe } from '../api/queries';
import { displayName, initialOf } from '../api/format';
import { useSurface } from '../hooks/useSurface';
import { signOut, useTelegramContext } from '../hooks/useTelegramContext';
import { openExternal } from '../telegram/sdk';
import { SKINS, useTheme, type FamilyPref, type SchemePref } from '../theme';
import { compareUrl, isDevBuild, versionLabel } from '../version';

const SCHEME_OPTIONS: { value: SchemePref; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/** The swatch previews the family's accent, so the two rows are told apart by
 *  colour rather than by name alone. */
const FAMILY_OPTIONS: { value: FamilyPref; label: string; swatch?: string }[] = [
  { value: 'system', label: 'Automatic' },
  { value: 'ios', label: 'iOS', swatch: SKINS['ios-light'].swatch },
  { value: 'material', label: 'Material', swatch: SKINS['mat-light'].swatch },
];

function AppearanceSection() {
  const { preference, setPreference, followsHost, skin } = useTheme();
  const { isTelegram } = useSurface();

  return (
    <>
      <SectionHeader>Appearance</SectionHeader>
      <GroupCard>
        <div role="radiogroup" aria-label="Colour scheme">
          {SCHEME_OPTIONS.map((option) => (
            <OptionRow
              key={option.value}
              label={option.label}
              hint={
                option.value === 'system'
                  ? isTelegram
                    ? "Follow Telegram's theme"
                    : "Follow your device's theme"
                  : undefined
              }
              selected={preference.scheme === option.value}
              onSelect={() => setPreference({ scheme: option.value })}
            />
          ))}
        </div>
      </GroupCard>
      {/* The one genuinely surprising consequence, so it is stated rather than
          left to be discovered: a pinned scheme stops tracking the host, and
          inside Telegram that means the app deliberately stops matching it. */}
      {isTelegram && !followsHost ? (
        <SectionFootnote>
          Using this app’s own colours instead of Telegram’s. Choose System on both rows to match
          Telegram again.
        </SectionFootnote>
      ) : null}

      <SectionHeader>Style</SectionHeader>
      <GroupCard>
        <div role="radiogroup" aria-label="Style">
          {FAMILY_OPTIONS.map((option) => (
            <OptionRow
              key={option.value}
              label={option.label}
              swatch={option.swatch}
              hint={option.value === 'system' ? 'Match your platform' : undefined}
              selected={preference.family === option.value}
              onSelect={() => setPreference({ family: option.value })}
            />
          ))}
        </div>
      </GroupCard>
      <SectionFootnote>
        Currently showing <strong>{SKINS[skin].label}</strong>. iOS uses tighter corners and a black
        dark mode; Material uses rounder corners and a navy one.
      </SectionFootnote>
    </>
  );
}

/**
 * Which build is this, and is it the current one?
 *
 * The version is a button rather than a label because the second question is
 * the one worth answering: it opens GitHub's comparison between this build's
 * commit and master, which says in a sentence whether anything newer exists.
 * A version number on its own cannot tell you that.
 */
function AboutSection() {
  const url = compareUrl();

  return (
    <>
      <SectionHeader>About</SectionHeader>
      <GroupCard>
        {url ? (
          <button
            type="button"
            className="settings-row"
            // Not an <a target="_blank">: inside Telegram that either gets
            // swallowed or replaces the Mini App with no way back.
            onClick={() => openExternal(url)}
          >
            <span className="settings-row__label">Version</span>
            <span className="settings-row__value">{versionLabel()}</span>
            <span style={{ color: 'var(--tg-hint)', marginLeft: 8 }}>›</span>
          </button>
        ) : (
          <div className="settings-row">
            <span className="settings-row__label">Version</span>
            <span className="settings-row__value">{versionLabel()}</span>
          </div>
        )}
      </GroupCard>
      <SectionFootnote>
        {isDevBuild
          ? 'Running from a dev server, so there is no released build to compare against.'
          : 'Tap to see what has changed on master since this build. An empty comparison means you have the newest version.'}
      </SectionFootnote>
    </>
  );
}

export function SettingsScreen() {
  const { data: me, isPending, isError, error, refetch } = useMe();
  const { isDesktop, isTelegram } = useSurface();
  const ctx = useTelegramContext();
  const { showToast } = useToast();
  const [exporting, setExporting] = useState(false);

  return (
    <>
      {isError ? <ErrorBanner message={error.message} onRetry={() => void refetch()} /> : null}

      <div className={isDesktop ? 'screen-content content' : 'screen-content'}>
        {isPending ? (
          <ListSkeleton />
        ) : (
          <>
            <div className="settings__profile">
              <div className="avatar avatar--profile">{initialOf(me?.name)}</div>
              <div className="settings__name">{me ? displayName(me.name, me.id) : ''}</div>
              {/* The bot stores ids, not names, so both of these can be null. */}
              <div className="settings__username">{me?.username ? `@${me.username}` : ''}</div>
            </div>

            <SectionHeader>Account</SectionHeader>
            <GroupCard>
              <div className="settings-row">
                <span className="settings-row__label">Telegram ID</span>
                <span className="settings-row__value">{me?.id}</span>
              </div>
              {/* `/api/export` is authenticated like every other route, so it
                  cannot be handed to `window.open` — that request would arrive
                  with no auth header and 401. Fetch it, then save the blob. */}
              <button
                type="button"
                className="settings-row settings-row--link"
                disabled={exporting}
                onClick={() => {
                  setExporting(true);
                  downloadExport()
                    .then(() => showToast('Export downloaded'))
                    .catch((err: unknown) =>
                      showToast(err instanceof Error ? err.message : "Couldn't export your data"),
                    )
                    .finally(() => setExporting(false));
                }}
              >
                <span className="settings-row__label">
                  {exporting ? 'Preparing export…' : 'Export my data'}
                </span>
                <span style={{ color: 'var(--tg-hint)' }}>›</span>
              </button>
            </GroupCard>
          </>
        )}

        {/* Outside the `isPending` gate on purpose: appearance is client-side
            state, so it stays usable when `/api/me` is slow or down — which is
            exactly when someone might be sitting on this screen. */}
        <AppearanceSection />

        <AboutSection />

        {/* Signing out is a browser-session concept; inside Telegram the
            account is the host's, so the row is not rendered there. Also
            outside the loading gate: a stuck or failing session is a reason to
            sign out, not a reason to hide the button. */}
        {!isTelegram && ctx.mode === 'browser' ? (
          <>
            <div style={{ height: 22 }} />
            <GroupCard>
              <button
                type="button"
                className="settings-row settings-row--danger"
                onClick={() => signOut()}
              >
                Sign out
              </button>
            </GroupCard>
          </>
        ) : null}
      </div>
    </>
  );
}
